import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { accessLogService, AccessLog, AccessLogFilters } from '../services/accessLogService';
import { toast } from 'react-hot-toast';
import { Search, RefreshCw, ShieldCheck, ShieldX, Monitor, X } from 'lucide-react';

const LIMIT = 50;

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const methodLabel: Record<string, string> = {
  email_password: 'Email/Senha',
  google_oauth: 'Google',
  microsoft_oauth: 'Microsoft',
};

const failureLabel: Record<string, string> = {
  invalid_credentials: 'Credenciais inválidas',
  inactive_account: 'Conta inativa',
};

const AccessLogs: React.FC = () => {
  const { capabilities } = useAuth();
  const navigate = useNavigate();

  const [logs, setLogs] = useState<AccessLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [emailFilter, setEmailFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedAgent, setExpandedAgent] = useState<number | null>(null);

  const totalPages = Math.ceil(total / LIMIT);

  const fetchLogs = useCallback(
    async (filters: AccessLogFilters) => {
      setLoading(true);
      try {
        const result = await accessLogService.getAll(filters);
        setLogs(result.data);
        setTotal(result.total);
      } catch {
        toast.error('Erro ao carregar logs de acesso');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!capabilities.canManageDevTools) {
      toast.error('Acesso negado.');
      navigate('/dashboard');
      return;
    }
    fetchLogs({ page, limit: LIMIT, status: statusFilter || undefined, email: emailFilter || undefined });
  }, [capabilities, navigate, page, statusFilter, emailFilter, fetchLogs]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  const handleClearFilters = () => {
    setEmailFilter('');
    setStatusFilter('');
    setPage(1);
  };

  return (
    <div className="h-screen bg-canvas flex flex-col w-full overflow-hidden">
      <Header title="Gerenciar Logins" subtitle="Histórico de acessos à plataforma" />
      <main className="flex-1 p-6 w-full overflow-y-auto overflow-x-hidden">
          {/* Filters */}
          <div className="mb-4 flex flex-wrap gap-3 items-end">
            <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-0">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle w-4 h-4" />
                <input
                  type="text"
                  value={emailFilter}
                  onChange={(e) => setEmailFilter(e.target.value)}
                  placeholder="Buscar por email..."
                  className="w-full pl-9 pr-3 py-2 border border-border-strong rounded-[5px] text-sm focus:outline-none focus:ring-2 focus:ring-[#bfdbfe] dark:focus:ring-blue-800"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="border border-border-strong rounded-[5px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#bfdbfe] dark:focus:ring-blue-800"
              >
                <option value="">Todos os status</option>
                <option value="success">Sucesso</option>
                <option value="failure">Falha</option>
              </select>
              <button
                type="submit"
                className="bg-[#2563eb] hover:bg-[#1d55d6] text-white px-4 py-2 rounded-[5px] text-sm font-medium transition-colors"
              >
                Buscar
              </button>
              {(emailFilter || statusFilter) && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="flex items-center gap-1 text-muted hover:text-[#3d4c60] dark:hover:text-body/80 px-3 py-2 border border-border-strong rounded-[5px] text-sm transition-colors"
                >
                  <X className="w-4 h-4" />
                  Limpar
                </button>
              )}
            </form>
            <button
              type="button"
              onClick={() => fetchLogs({ page, limit: LIMIT, status: statusFilter || undefined, email: emailFilter || undefined })}
              className="flex items-center gap-2 text-[#5b6b82] dark:text-muted hover:text-body px-3 py-2 border border-border-strong rounded-[5px] text-sm transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Atualizar
            </button>
          </div>

          {/* Summary */}
          <p className="text-sm text-muted mb-3">
            {total} registro{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''}
          </p>

          {/* Table */}
          <div className="bg-surface rounded-[5px] shadow-sm border border-border-subtle overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2563eb]" />
              </div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-subtle">
                <Monitor className="w-12 h-12 mb-3 opacity-40" />
                <p>Nenhum log encontrado</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface-muted border-b border-border-subtle">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">Data/Hora</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">Usuário</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">IP</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">Método</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">Detalhe</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">User Agent</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-subtle">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-[#f9fbfd] dark:hover:bg-surface-muted transition-colors">
                        <td className="px-4 py-3 text-sm text-[#5b6b82] dark:text-muted whitespace-nowrap">
                          {formatDate(log.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-body">{log.user_name ?? '—'}</div>
                          <div className="text-xs text-muted">{log.user_email}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#5b6b82] dark:text-muted font-mono whitespace-nowrap">
                          {log.ip_address ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#5b6b82] dark:text-muted whitespace-nowrap">
                          {methodLabel[log.login_method] ?? log.login_method}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {log.status === 'success' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#e7f8ef] dark:bg-emerald-900/30 text-[#16a34a] dark:text-emerald-400">
                              <ShieldCheck className="w-3 h-3" />
                              Sucesso
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#fbe0e0] dark:bg-red-900/30 text-[#c0392b] dark:text-red-400">
                              <ShieldX className="w-3 h-3" />
                              Falha
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted whitespace-nowrap">
                          {log.failure_reason
                            ? (failureLabel[log.failure_reason] ?? log.failure_reason)
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-subtle max-w-xs">
                          {log.user_agent ? (
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedAgent(expandedAgent === log.id ? null : log.id)
                              }
                              className="text-left hover:text-[#5b6b82] dark:hover:text-muted transition-colors"
                              title={log.user_agent}
                            >
                              {expandedAgent === log.id
                                ? log.user_agent
                                : log.user_agent.slice(0, 40) + (log.user_agent.length > 40 ? '…' : '')}
                            </button>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-muted">
                Página {page} de {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm border border-border-strong rounded-[5px] disabled:opacity-40 hover:bg-[#f9fbfd] dark:hover:bg-surface-muted transition-colors"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1.5 text-sm border border-border-strong rounded-[5px] disabled:opacity-40 hover:bg-[#f9fbfd] dark:hover:bg-surface-muted transition-colors"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
      </main>
    </div>
  );
};

export default AccessLogs;
